import {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { ChartDataset, ChartOptions, Chart as ChartJS } from 'chart.js'

import { useSpendTooltip } from '../../SpendTooltip'
import { makePillPlugin } from '../../plugins/makePillPlugin'
import { makeRevealPlugin } from '../../plugins/makeRevealPlugin'
import type { ChartLegendItem } from '../../ChartLegend'

import type { SpendLineChartProps } from '../types'
import {
  LEGEND_ITEMS,
  MIN_POINT_WIDTH,
  Y_AXIS_WIDTH,
  buildGradient
} from '../constants'
import styles from '../styles.module.css'

export function useSpendLineChart({
  labels = [],
  cumulativeData = [],
  forecastData = [],
  goalData = [],
  bandHighData = [],
  bandLowData = [],
  elapsedIndex = -1,
  pillLabel = 'Hoje',
  epoch = 0,
  currency = 'BRL',
  hourly = false,
  endIndex = -1,
  endLabel = ''
}: SpendLineChartProps) {
  const id = useId().replaceAll(':', '')
  const chartRef = useRef<ChartJS<'line'>>(null)
  const mainScrollRef = useRef<HTMLDivElement>(null)
  const xAxisScrollRef = useRef<HTMLDivElement>(null)
  const scrollWrapperRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef({ active: false, startX: 0, startScrollLeft: 0 })
  const elapsedIndexRef = useRef(elapsedIndex)
  useLayoutEffect(() => {
    elapsedIndexRef.current = elapsedIndex
  })

  const { tooltipData, position, tooltipRef, externalTooltipHandler } =
    useSpendTooltip()

  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set())

  const toggleLegend = useCallback((key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const hasForecast = useMemo(
    () => forecastData.some(v => v !== null),
    [forecastData]
  )

  const legendItems = useMemo(
    (): ChartLegendItem[] =>
      hasForecast
        ? LEGEND_ITEMS
        : LEGEND_ITEMS.filter(i => i.key !== 'forecast'),
    [hasForecast]
  )

  const syncShadows = useCallback((el: HTMLDivElement) => {
    const w = scrollWrapperRef.current
    if (!w) return
    const hasScroll = el.scrollWidth > el.clientWidth
    el.classList.toggle(styles.scrollable, hasScroll)
    w.classList.toggle(styles.shadowLeft, el.scrollLeft > 0)
    w.classList.toggle(
      styles.shadowRight,
      hasScroll && el.scrollLeft < el.scrollWidth - el.clientWidth - 1
    )
  }, [])

  const handleMainScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (xAxisScrollRef.current) {
        xAxisScrollRef.current.scrollLeft = e.currentTarget.scrollLeft
      }
      syncShadows(e.currentTarget)
    },
    [syncShadows]
  )

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = mainScrollRef.current
    if (!el) return
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft
    }
    el.classList.add(styles.dragging)
  }, [])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!dragRef.current.active) return
      e.preventDefault()
      const el = mainScrollRef.current
      if (!el) return
      el.scrollLeft =
        dragRef.current.startScrollLeft - (e.clientX - dragRef.current.startX)
      if (xAxisScrollRef.current)
        xAxisScrollRef.current.scrollLeft = el.scrollLeft
      syncShadows(el)
    },
    [syncShadows]
  )

  const handleMouseUpOrLeave = useCallback(() => {
    dragRef.current.active = false
    mainScrollRef.current?.classList.remove(styles.dragging)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const el = mainScrollRef.current
    if (!el) return
    dragRef.current = { active: true, startX: e.touches[0].clientX, startScrollLeft: el.scrollLeft }
  }, [])

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!dragRef.current.active) return
      const el = mainScrollRef.current
      if (!el) return
      el.scrollLeft = dragRef.current.startScrollLeft - (e.touches[0].clientX - dragRef.current.startX)
      if (xAxisScrollRef.current) xAxisScrollRef.current.scrollLeft = el.scrollLeft
      syncShadows(el)
    },
    [syncShadows]
  )

  const handleTouchEnd = useCallback(() => {
    dragRef.current.active = false
  }, [])

  const yMax = useMemo(() => {
    const values = [
      ...goalData,
      ...cumulativeData,
      ...forecastData,
      ...bandHighData
    ].filter((v): v is number => v !== null)
    return values.length ? Math.max(...values) : 0
  }, [goalData, cumulativeData, forecastData, bandHighData])

  const plugins = useMemo(() => [makeRevealPlugin(id)], [id])

  const xAxisPlugins = useMemo(() => {
    const xPlugins = [makePillPlugin(id, pillLabel, () => elapsedIndexRef.current)]
    if (endIndex >= 0 && endLabel) {
      xPlugins.push(makePillPlugin(`end-${id}`, endLabel, () => endIndex, true))
    }
    return xPlugins
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pillLabel, endIndex, endLabel])

  const datasets: ChartDataset<'line'>[] = useMemo(
    () => [
      {
        label: '_bandHigh',
        data: bandHighData,
        fill: '+1',
        backgroundColor: 'rgba(3,102,221,0.14)',
        borderWidth: 0,
        pointRadius: 0,
        spanGaps: false,
        clip: false,
        hidden: !hasForecast || hiddenKeys.has('forecast')
      } as ChartDataset<'line'>,
      {
        label: '_bandLow',
        data: bandLowData,
        fill: false,
        borderWidth: 0,
        pointRadius: 0,
        spanGaps: false,
        clip: false,
        hidden: !hasForecast || hiddenKeys.has('forecast')
      } as ChartDataset<'line'>,
      {
        label: 'Meta de consumo total',
        data: goalData,
        borderColor: LEGEND_ITEMS[0].color,
        fill: false,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
        spanGaps: false,
        clip: false,
        hidden: hiddenKeys.has('goal')
      },
      {
        label: 'Previsão',
        data: forecastData,
        borderColor: LEGEND_ITEMS[2].color,
        borderDash: [6, 4],
        fill: true,
        backgroundColor: ctx => {
          const chart = ctx.chart
          const { chartArea } = chart
          if (!chartArea) return 'rgba(3,102,221,0.035)'
          return buildGradient(chart.ctx, chartArea, false)
        },
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        spanGaps: false,
        clip: false,
        hidden: !hasForecast || hiddenKeys.has('forecast')
      },
      {
        label: 'Realizado',
        data: cumulativeData,
        borderColor: LEGEND_ITEMS[1].color,
        fill: true,
        backgroundColor: ctx => {
          const chart = ctx.chart
          const { chartArea } = chart
          if (!chartArea) return 'rgba(3,102,221,0.10)'
          return buildGradient(chart.ctx, chartArea, true)
        },
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        spanGaps: false,
        clip: false,
        hidden: hiddenKeys.has('cumulative')
      }
    ],
    [
      bandHighData,
      bandLowData,
      goalData,
      cumulativeData,
      forecastData,
      hiddenKeys,
      hasForecast
    ]
  )

  const yAxisDatasets: ChartDataset<'line'>[] = useMemo(
    () => [
      {
        label: '_scale',
        data: goalData,
        borderWidth: 0,
        pointRadius: 0,
        borderColor: 'transparent' as const,
        backgroundColor: 'transparent' as const
      }
    ],
    [goalData]
  )

  const xAxisDatasets: ChartDataset<'line'>[] = useMemo(
    () => [
      {
        label: '_x',
        data: goalData,
        borderWidth: 0,
        pointRadius: 0,
        borderColor: 'transparent' as const,
        backgroundColor: 'transparent' as const
      }
    ],
    [goalData]
  )

  const showAnnotation = elapsedIndex >= 0 && (!hourly || elapsedIndex < labels.length - 1)

  const yAxisOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      transitions: {
        active: { animation: { duration: 0 } },
        show: { animation: { duration: 0 } },
        hide: { animation: { duration: 0 } },
        resize: { animation: { duration: 0 } }
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        annotation: { annotations: {} }
      },
      layout: { padding: { left: 0, right: 0, top: 13, bottom: 8 } },
      scales: {
        x: { display: false },
        y: {
          min: 0,
          suggestedMax: yMax,
          grid: { color: '#f2f2f2' },
          border: { display: false },
          ticks: {
            count: 5,
            callback: v =>
              new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency,
                maximumFractionDigits: 0
              }).format(v as number),
            font: { size: 12 },
            color: '#707070',
            padding: 3
          },
          afterFit(scale) {
            scale.width = Y_AXIS_WIDTH
          }
        }
      }
    }),
    [yMax, currency]
  )

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 10, right: 20, top: 13, bottom: 8 } },
      interaction: { mode: 'index', intersect: false },
      animation: false,
      transitions: {
        active: { animation: { duration: 0 } },
        show: { animation: { duration: 0 } },
        hide: { animation: { duration: 0 } },
        resize: { animation: { duration: 0 } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler as never
        },
        annotation: {
          annotations: {
            ...(showAnnotation ? {
              todayLine: {
                type: 'line',
                xMin: elapsedIndex,
                xMax: elapsedIndex,
                borderColor: '#707070',
                borderWidth: 1,
                borderDash: [2, 2]
              }
            } : {}),
            ...(endIndex >= 0 ? {
              endLine: {
                type: 'line',
                xMin: endIndex,
                xMax: endIndex,
                borderColor: '#707070',
                borderWidth: 1,
                borderDash: [2, 2]
              }
            } : {})
          }
        },
        [`reveal-${id}`]: { epoch }
      },
      scales: {
        x: { display: false, offset: true },
        y: {
          min: 0,
          suggestedMax: yMax,
          grid: { color: '#f2f2f2' },
          border: { display: false },
          ticks: { display: false },
          afterFit(scale) {
            scale.width = 0
          }
        }
      }
    }),
    [elapsedIndex, showAnnotation, externalTooltipHandler, epoch, id, yMax, endIndex, endLabel]
  )

  const xAxisOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      transitions: {
        active: { animation: { duration: 0 } },
        show: { animation: { duration: 0 } },
        hide: { animation: { duration: 0 } },
        resize: { animation: { duration: 0 } }
      },
      layout: { padding: { top: 0, right: 20, bottom: 0, left: 10 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        annotation: { annotations: {} }
      },
      scales: {
        x: {
          offset: true,
          grid: { display: false },
          border: { display: false },
          afterFit(scale) {
            scale.height = 40
          },
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            callback(_value, index) {
              if (hourly) return index % 2 === 0 ? labels[index] : ''
              if (index === 0) return elapsedIndex === 0 ? '' : labels[0]
              return ''
            },
            font: { size: 12 },
            color: '#707070',
            padding: 2
          }
        },
        y: {
          display: false,
          afterFit(scale) {
            scale.width = 0
          }
        }
      }
    }),
    [labels, hourly, elapsedIndex]
  )

  const chartMinWidth = useMemo(
    () => labels.length * MIN_POINT_WIDTH,
    [labels.length]
  )

  return {
    id,
    chartRef,
    mainScrollRef,
    xAxisScrollRef,
    scrollWrapperRef,
    elapsedIndexRef,
    tooltipData,
    position,
    tooltipRef,
    hiddenKeys,
    legendItems,
    datasets,
    yAxisDatasets,
    xAxisDatasets,
    options,
    yAxisOptions,
    xAxisOptions,
    plugins,
    xAxisPlugins,
    chartMinWidth,
    syncShadows,
    handleMainScroll,
    handleMouseDown,
    handleMouseMove,
    handleMouseUpOrLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    toggleLegend
  }
}
